import { checkEmail, checkPassword } from '@shef/validators';
import { checkFirstAndLastName } from '@shef/validators/dist/src/checkName';
import { ZIP_CODE_REGEX } from 'common/Constants';
import { ShefErrorCode } from 'common/errors/ShefErrors';
import { ClientEvents } from 'common/events/ClientEvents';
import { isLeft, isRight } from 'fp-ts/lib/Either';
import { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { isShefApiResponseErrorWithCode } from 'src/error/isShefApiResponseError';
import { CurrentUserFieldsFragment, useRegisterMutation } from 'src/gqlReactTypings.generated.d';
import {
  LEAD_CAPTURE_CONTACT_STORAGE_KEY,
  LeadCaptureContact,
} from 'src/pages/consumer/lead-capture-modal/leadCaptureStorage';
import { useFormattedPhoneInput } from 'src/shared/hooks/useFormattedPhoneInput';
import { useTextInputCallback } from 'src/shared/hooks/useInputCallback';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { isValidPhone } from 'src/shared/utils/PhoneUtils';
import { UserPreferences } from 'src/user-preferences/userPreferencesTypes';
import { usePromotionalsHook } from '../../promotionals/usePromotionalsHook';
import { useTracker } from '../../shared/hooks/useTracker';
import { useUserPreferences } from '../../user-preferences/useUserPreferences';
import { useHandleLogin } from '../useHandleLogin';
import { useGateType } from './GateTypeContext';
import { useLeadCaptureAutofill } from './useLeadCaptureAutofill';

export interface SignUpWithEmailProps {
  email: string;
  onEmailChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onSuccess?: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void;
  onUserAlreadyExists: (email: string, errorMessage: string) => void;
  requireZipCode?: boolean;
  signUpPromoCode?: string;
  signUpEntryPoint?: string | null;
  signupCTA?: string | null;
}

const GENERIC_SIGN_UP_MESSAGE = 'There was an error signing up. Please try again.';

/**
 * Everything the sign-up screen does, with nothing about how it looks. Control's modal joins first
 * and last name into one DualLabeledTextInput; the gate frames draw two separate fields. Sharing
 * this keeps that the only difference between the arms.
 */
export const useSignUpWithEmailForm = ({
  email,
  onEmailChange,
  onSuccess,
  onUserAlreadyExists,
  requireZipCode,
  signUpPromoCode,
  signUpEntryPoint,
}: SignUpWithEmailProps) => {
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | undefined>();
  const [nameErrorMessage, setNameErrorMessage] = useState<string | undefined>();
  const [zipCodeErrorMessage, setZipCodeErrorMessage] = useState<string | undefined>();
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | undefined>();
  const [phoneErrorMessage, setPhoneErrorMessage] = useState<string | undefined>();
  const [signUpErrorMessage, setSignUpErrorMessage] = useState<string | undefined>();
  const [tosChecked, setTosChecked] = useState(false);
  const [firstName, onFirstNameChange] = useTextInputCallback('');
  const [lastName, onLastNameChange] = useTextInputCallback('');
  const [zipCode, onZipCodeChange] = useTextInputCallback('');
  const [leadContact, setLeadContact] = useLocalStorage<LeadCaptureContact>(LEAD_CAPTURE_CONTACT_STORAGE_KEY, null);
  const [displayPhone, onPhoneChange, rawPhone] = useFormattedPhoneInput('');
  const [password, onPasswordChange] = useTextInputCallback('');
  const [hadLeadContact] = useState(() => Boolean(leadContact));

  const [{ utm, [UserPreferences.ZIPCODE]: cachedZipCode }] = useUserPreferences();
  const { code } = usePromotionalsHook();
  const promoCode = code?.code;

  const tracker = useTracker();
  const gateType = useGateType();
  const handleLogin = useHandleLogin();

  useEffect(() => {
    tracker.track(ClientEvents.LOGIN_MODAL_SIGNUP_PAGE, {
      signUpEntryPoint,
      method: 'email',
      gateType,
    });
  }, [tracker, signUpEntryPoint, gateType]);

  useLeadCaptureAutofill({ email, onEmailChange, phone: displayPhone, onPhoneChange });

  const [registerMutation, { loading }] = useRegisterMutation();

  const register = () =>
    registerMutation({
      variables: {
        userInput: {
          email,
          password,
          firstName,
          lastName,
          phone: rawPhone,
          hasAcceptedTermsOfService: tosChecked,
          hasOptedIntoMarketingSms: tosChecked,
        },
        utm,
        zipCode: zipCode || cachedZipCode,
        promoCode: signUpPromoCode ?? promoCode,
        signUpEntryPoint: signUpEntryPoint ?? null,
      },
    })
      .then(({ data, errors }) => {
        if (isNil(data) || !isNil(errors)) {
          const invalidEmailError = errors?.find((error) =>
            isShefApiResponseErrorWithCode(error, ShefErrorCode.INVALID_EMAIL)
          );
          const accountAlreadyExistsError = errors?.find((error) =>
            isShefApiResponseErrorWithCode(error, ShefErrorCode.SIGN_UP_USER_ALREADY_EXISTS)
          );
          if (!isNil(accountAlreadyExistsError)) {
            onUserAlreadyExists(email, 'An account with this email already exists. Please log in.');
          } else if (!isNil(invalidEmailError)) {
            setEmailErrorMessage(invalidEmailError.message);
          } else {
            setSignUpErrorMessage(GENERIC_SIGN_UP_MESSAGE);
          }
        } else {
          if (hadLeadContact) {
            setLeadContact(null);
          }
          handleLogin('email', data.register);

          if (onSuccess) {
            onSuccess(data.register.isExistingUser, data.register.user);
          }
        }
      })
      .catch((error) => {
        setSignUpErrorMessage(GENERIC_SIGN_UP_MESSAGE);
        tracker.track(ClientEvents.SIGN_UP_ERROR, {
          method: 'email',
          errors: [error.message],
        });
      });

  const validateForm = () => {
    setEmailErrorMessage(undefined);
    setNameErrorMessage(undefined);
    setZipCodeErrorMessage(undefined);
    setPasswordErrorMessage(undefined);
    setPhoneErrorMessage(undefined);
    setSignUpErrorMessage(undefined);

    const emailCheck = checkEmail(email);
    if (isLeft(emailCheck)) {
      setEmailErrorMessage(emailCheck.left);
    }

    const nameCheck = checkFirstAndLastName({ firstName, lastName });
    if (isLeft(nameCheck)) {
      setNameErrorMessage(nameCheck.left);
    }

    const isZipCodeValid = !requireZipCode || ZIP_CODE_REGEX.test(zipCode);
    if (!isZipCodeValid) {
      setZipCodeErrorMessage('Please enter a valid zip code');
    }

    const isPhoneValid = isValidPhone(rawPhone);
    if (!isPhoneValid) {
      setPhoneErrorMessage('Please enter a valid phone number');
    }

    const passwordCheck = checkPassword(password);
    if (isLeft(passwordCheck)) {
      setPasswordErrorMessage(passwordCheck.left);
    }

    if (!tosChecked) {
      setSignUpErrorMessage('You must accept our Terms of Service to register');
    }

    return (
      isRight(emailCheck) &&
      isRight(nameCheck) &&
      isZipCodeValid &&
      isPhoneValid &&
      isRight(passwordCheck) &&
      tosChecked
    );
  };

  const handleSubmitForm = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (loading) {
      return;
    }

    const isFormValidated = validateForm();
    if (isFormValidated) {
      tracker.track(ClientEvents.LOGIN_MODAL_SIGNUP_SUBMIT, { method: 'email' });
      register();
    }
  };

  // Theoretically this shouldn't be needed – not sure why enter isn't handling form submission like normal
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitForm();
    }
  };

  return {
    firstName,
    onFirstNameChange,
    lastName,
    onLastNameChange,
    zipCode,
    onZipCodeChange,
    displayPhone,
    onPhoneChange,
    password,
    onPasswordChange,
    tosChecked,
    toggleTos: () => setTosChecked((checked) => !checked),
    emailErrorMessage,
    nameErrorMessage,
    zipCodeErrorMessage,
    passwordErrorMessage,
    phoneErrorMessage,
    signUpErrorMessage,
    loading,
    handleSubmitForm,
    handleKeyDown,
  };
};
